// chat.js: Componente principal de chat con entrada de voz, subida de archivos y respuestas del asistente

import axios from 'axios';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { chatgpt_logo } from '../assets';
import ChatItem from '../components/ChatItems';
import { ASSISTANT_CHAT_HISTORY_URL, ASSISTANT_CHAT_SEND_URL } from '../constants';
import { AuthContext } from '../context/AuthContext';
import { ChatContext } from '../context/ChatContext';

const Chat = ({ time }) => {
  // Contextos para manejar estado global
  const { chatMessage, setChatMessage, setChatShow, setShowChatTime, setChatMenu, showChatTime } = useContext(ChatContext);
  const { nom, curso } = useContext(AuthContext);

  // Estados locales
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [responses, setResponses] = useState([]);
  const [chatPower, setChatPower] = useState([]);
  const [files, setFiles] = useState([]);

  // Refs
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Focus helper
  const focusTextarea = () => {
    // Pequeño timeout ayuda cuando el DOM acaba de actualizar
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  // Obtener historial de chat desde backend
  useEffect(() => {
    const fetchChatHistory = async () => {
      try {
        const response = await axios.get(ASSISTANT_CHAT_HISTORY_URL, {
          params: { userid: nom },
        });
        setChatPower(response.data.chatHistory);
      } catch (error) {
        console.error('Error fetching chat history:', error.message);
      } finally {
        focusTextarea();
      }
    };
    if (nom) fetchChatHistory();
  }, [nom]);

  // Scroll automático
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [responses, chatPower]);

  // Reset cuando cambia chatMessage externo
  useEffect(() => {
    setResponses([]);
    setMessage('');
    setFiles([]);
    adjustTextareaHeight();
    focusTextarea();
  }, [chatMessage]);

  // Enviar mensaje y archivos
  const handleSend = async (forcedMessage) => {
    const finalMessage = typeof forcedMessage === 'string' ? forcedMessage : message;
    if (loading) return;
    if (!finalMessage.trim() && !files.length) return;

    const now = new Date();
    const filenames = files.map((file) => file.name);

    const tempResponse = {
      message: finalMessage,
      response: 'Procesando respuesta...',
      filenames,
      timestamp: now.toISOString(),
      pending: true,
    };
    setResponses((prev) => [...prev, tempResponse]);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('userId', nom);
      formData.append('time', time || showChatTime);
      const fullMessage = finalMessage + ' ' + (curso?.['NOMBRELARGOCURSO'] || '');
      formData.append('message', fullMessage);

      files.forEach((file) => formData.append('files', file));

      const res = await axios.post(ASSISTANT_CHAT_SEND_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });

      const returnedFilenames = res.data.filenames || [];

      setResponses((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          response: res.data.answer,
          filenames: returnedFilenames,
          citations: res.data.citations || [],
          pending: false,
        };
        return updated;
      });

      setMessage('');
      setFiles([]);
      // Siempre vuelve el foco al textarea
      focusTextarea();
    } catch (err) {
      console.error('Error sending message:', err);
      setResponses((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          response: err.response?.data?.error || 'Error al procesar la respuesta.',
          pending: false,
        };
        return updated;
      });
      focusTextarea();
    } finally {
      if (chatMessage.length === 0) {
        try {
          const response = await axios.get(ASSISTANT_CHAT_HISTORY_URL, { params: { userid: nom } });
          setChatMenu(response.data.menu.reverse());
        } catch (error) {
          console.error('Error fetching chat history:', error.message);
        }
      }
      setLoading(false);
      focusTextarea();
    }
  };

  // Nuevo diálogo
  const handleNewDialog = () => {
    setShowChatTime(new Date().toISOString());
    setChatMessage([]);
    setChatShow(true);

    // Limpieza inmediata
    setResponses([]);
    setFiles([]);
    setMessage('');
    focusTextarea();

    const fetchChatHistory = async () => {
      try {
        const response = await axios.get(ASSISTANT_CHAT_HISTORY_URL, {
          params: { userid: nom },
        });
        setChatMenu(response.data.menu);
      } catch (error) {
        console.error('Error fetching chat history:', error.message);
      } finally {
        focusTextarea();
      }
    };
    fetchChatHistory();
  };

  // Inputs
  const handleInputChange = (e) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
    focusTextarea();
  };

  const removeFile = (index) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next;
    });
    focusTextarea();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
  };

  const formatTimestamp = (timestamp) => moment(timestamp).format('MMMM Do YYYY, h:mm:ss a');

  return (
    <>
      {/* Historial y respuestas */}
      <div className="flex flex-col flex-grow overflow-y-auto w-full mx-auto p-4 bg-white">
        <div className="flex-grow overflow-y-auto w-full mx-auto">
          {chatMessage.map((item, index) => (
            <ChatItem
              key={`history-${index}`}
              index={index}
              timestamp={formatTimestamp(item.timestamp)}
              title={item.question}
              content={item.answer}
              filenames={item.filenames}
              citations={item.citations}
              chatgpt_logo={chatgpt_logo}
            />
          ))}

          {responses.map((item, index) => (
            <ChatItem
              key={`response-${index}`}
              index={index + chatMessage.length}
              timestamp={formatTimestamp(item.timestamp)}
              title={item.message}
              content={item.response}
              filenames={item.filenames}
              citations={item.citations}
              chatgpt_logo={chatgpt_logo}
            />
          ))}

          {loading && (
            <div className="flex justify-center items-center py-4">
              <div className="loader">
                Cargando...
                <i className="fas fa-spinner fa-spin text-2xl text-gray-500"></i>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Botones inferiores (solo "Nuevo diálogo") */}
      <div className="w-4/5 bottom-0 bg-white flex items-center space-x-3 mx-auto text-black text-[15px]">
        <div className="flex-grow" />
        <div className="flex items-end space-x-3 flex-wrap gap-2">
          <button onClick={handleNewDialog} className="p-2 sm:p-3 bg-white-500 text-black">
            <i className="fas fa-broom"></i> &nbsp; Nuevo diálogo
          </button>
          {/* Botones de Socrático/Voz/Hablar ocultos por solicitud */}
        </div>
      </div>

      {/* Área de texto + carga de archivos */}
      <div className="w-full p-4">
        <div className="w-full bottom-0 mb-3 bg-white p-3 sm:p-4 flex items-center space-x-3 border rounded-lg border-gray mx-auto">
          <div className="flex-grow">
            {loading ? (
              <div className="loader">
                <i className="fas fa-spinner fa-spin text-2xl text-gray-500"></i> &nbsp; Generar mensaje...
              </div>
            ) : (
              <>
                {/* Archivos seleccionados */}
                <div className={`flex space-x-2 ${files.length ? 'mb-4' : ''}`}>
                  {files.map((file, index) => (
                    <div key={index} className="rounded-lg border p-2 whitespace-nowrap">
                      <i className="fa-regular fa-file p-2 bg-blue-500 me-2 rounded-lg text-white"></i>
                      {file.name}
                      <button onClick={() => removeFile(index)} className="ms-2 cursor-pointer text-xs">
                        ❌
                      </button>
                    </div>
                  ))}
                </div>

                {/* Textarea */}
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className="w-full bg-white focus:outline-none focus:border-white-400"
                  rows="1"
                  placeholder="Envíame un mensaje para ayudarte. Soy tu asistente virtual..."
                  style={{ maxHeight: '150px', overflow: 'auto' }}
                />
              </>
            )}
          </div>

          {/* Enviar / Adjuntar */}
          <div className="flex items-center space-x-3">
            {loading ? (
              <button className="p-2 bg-white-500 text-gray" disabled>
                <i className="fas fa-stop"></i>
              </button>
            ) : (
              <>
                <label htmlFor="file-upload" className="cursor-pointer" title="Adjuntar archivos">
                  <i className="fas fa-paperclip text-xl p-2"></i>
                </label>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  // accept=".pdf,.doc,.docx,.txt" // opcional
                />
                <button onClick={() => handleSend()} disabled={loading} className="p-2 bg-white-500 text-black" title="Enviar">
                  <i className="fas fa-paper-plane"></i>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Chat;
